import React, { useEffect, useMemo, useState } from 'react';
import {
    Continued,
    CTableWrapper,
    DBlock,
    DCheckbox,
    DeleteIcon,
    DIdAutocomplete,
    Discontinued,
    DPaginator,
    DTextField,
    EditIcon,
    usePaginator,
} from '../../../../../dum';
import { Box, IconButton, Typography } from '@mui/material';
import {
    MachinesPageQuery,
    useMachinesPageLazyQuery,
} from '../../../../../../services';
import {
    COLUMN_LABELS,
    FIELD_LABELS,
    shortLabel,
    useBoolean,
    useDebounce,
    useNumber,
    useString,
} from '../../../../../../helpers';
import {
    SDeleteMachineDialog,
    SUpsertMachineDialog,
} from '../../../../../smart';

export function MachinesListBlock() {
    const [query, { data, previousData, loading }] =
        useMachinesPageLazyQuery();
    const [machineId, setMachineId] = useState<number | null>(null);
    const [upsertOpen, setUpsertOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [orderProductionTypeId, setOrderProductionTypeId] = useNumber({
        KEY: MachinesListBlock.name + '_MachineTypeId',
    });
    const [filter, setFilter] = useString({
        KEY: MachinesListBlock.name + '_Filter',
    });

    const debouncedFilter = useDebounce(filter, 400);

    const [branchId, setBranchId] = useNumber({
        KEY: MachinesListBlock.name + '_BranchId',
    });

    const [showDiscontinued, setShowDiscontinued] = useBoolean({
        KEY: MachinesListBlock.name + '_ShowDiscontinued',
        defaultValue: true,
    });

    const { take, setSkip, skip } = usePaginator(
        {
            KEY: MachinesListBlock.name + '_Paginator',
        },
        [branchId, debouncedFilter, orderProductionTypeId, showDiscontinued],
    );

    useEffect(() => {
        void query({
            variables: {
                skip: skip,
                take: take,
                filter: debouncedFilter,
                branchId: branchId,
                orderProductionTypeId: orderProductionTypeId,
                discontinued: showDiscontinued ? null : false,
            },
        });
    }, [query, skip, take, branchId, debouncedFilter, orderProductionTypeId, showDiscontinued]);

    const handleEditMachine = useMemo(() => {
        return (
            machine: MachinesPageQuery['paginatedMachines']['docs'][number],
        ) => {
            setMachineId(machine.id);
            setUpsertOpen(true);
        };
    }, []);

    const handleDeleteMachine = useMemo(() => {
        return (
            machine: MachinesPageQuery['paginatedMachines']['docs'][number],
        ) => {
            setMachineId(machine.id);
            setDeleteOpen(true);
        };
    }, []);

    return (
        <DBlock>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Máquinas</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <DTextField value={filter} onChange={setFilter} label={'Filtro'} helperText={'Nombre de la máquina.'} hideHelperTextBelowLg />
                </Box>
                <DPaginator
                    skip={skip}
                    take={take}
                    setSkip={setSkip}
                    disableGutters
                    count={
                        data && data?.paginatedMachines.count >= 0
                            ? data?.paginatedMachines.count
                            : previousData?.paginatedMachines.count
                    }
                />
            </Box>
            <CTableWrapper
                contextItems={[
                    {
                        title: shortLabel(COLUMN_LABELS.edit),
                        callback: (machine) => {
                            handleEditMachine(machine);
                        },
                    },
                    {
                        title: shortLabel(COLUMN_LABELS.delete),
                        disable: (machine) => !machine.is_deletable,
                        callback: (machine) => {
                            handleDeleteMachine(machine);
                        },
                    },
                ]}
                items={[
                    {
                        title: shortLabel(FIELD_LABELS.name),
                        cellGrow: 20,
                        content: (machine) => machine.name,
                    },
                    {
                        title: shortLabel(
                            FIELD_LABELS.order_production_type_id,
                        ),
                        cellGrow: 20,
                        filter: (
                            <Box sx={{ width: '100%', '& .MuiTextField-root': { minWidth: 0 } }}>
                                <DIdAutocomplete
                                    id={orderProductionTypeId}
                                    onChange={setOrderProductionTypeId}
                                    itemText={'name'}
                                    label={'Tipo'}
                                    options={data?.getOrderProductionTypes || []}
                                    disableGutters
                                />
                            </Box>
                        ),
                        content: (machine) => machine.order_production_type?.name,
                    },
                    {
                        title: shortLabel(FIELD_LABELS.branch_id),
                        cellGrow: 20,
                        filter: (
                            <Box sx={{ width: '100%', '& .MuiTextField-root': { minWidth: 0 } }}>
                                <DIdAutocomplete
                                    id={branchId}
                                    onChange={setBranchId}
                                    itemText={'name'}
                                    label={'Sucursal'}
                                    options={data?.getBranches || []}
                                    disableGutters
                                />
                            </Box>
                        ),
                        content: (machine) => machine.branch?.name,
                    },
                    {
                        title: shortLabel(FIELD_LABELS.discontinued),
                        textAlign: 'center',
                        filter: (
                            <DCheckbox
                                onChange={setShowDiscontinued}
                                value={showDiscontinued}
                                label={'Discontinuadas'}
                                disableGutters
                            />
                        ),
                        content: (machine) =>
                            !machine.discontinued ? (
                                <Continued color={'success'} />
                            ) : (
                                <Discontinued color={'error'} />
                            ),
                    },
                    {
                        title: shortLabel(FIELD_LABELS.updated_by_id),
                        content: (machine) => machine.updated_by?.fullname || '—',
                    },
                    {
                        title: '',
                        content: (machine) => (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton
                                    size="small"
                                    sx={{ bgcolor: 'rgba(34,197,94,0.15)', boxShadow: 2, borderRadius: '50%' }}
                                    onClick={() => handleEditMachine(machine)}
                                >
                                    <EditIcon />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    sx={{ bgcolor: 'rgba(239,68,68,0.15)', boxShadow: 2, borderRadius: '50%' }}
                                    disabled={!machine.is_deletable}
                                    onClick={() => handleDeleteMachine(machine)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        ),
                    },
                ]}
                data={
                    data?.paginatedMachines.docs ||
                    previousData?.paginatedMachines.docs ||
                    []
                }
                loading={loading}
            />
            <SUpsertMachineDialog
                open={upsertOpen}
                setOpen={setUpsertOpen}
                machineId={machineId}
            />
            <SDeleteMachineDialog
                open={deleteOpen}
                setOpen={setDeleteOpen}
                machineId={machineId}
            />
        </DBlock>
    );
}
